import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { catchError, finalize, forkJoin, map, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Environment, environments } from '../configuration-view';
import { SourceAuthService } from '../source-auth.service';

interface EnvLogin {
  username: string;
  password: string;
  visible: boolean;
  pending: boolean;
  error: string | null;
}

interface AccountLogin {
  username: string;
  password: string;
  visible: boolean;
  pending: boolean;
}

function emptyLogin(): EnvLogin {
  return { username: '', password: '', visible: false, pending: false, error: null };
}

function emptyAccount(): AccountLogin {
  return { username: '', password: '', visible: false, pending: false };
}

@Component({
  selector: 'app-login',
  imports: [FormsModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule, TranslocoPipe],
  templateUrl: './login-page.html',
  styleUrl: './login-page.scss',
})
export class LoginPage {
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly auth = inject(SourceAuthService);
  private readonly router = inject(Router);
  readonly activeLang = this.transloco.activeLang;
  readonly langs = ['pl', 'en'] as const;
  readonly environments = environments;
  readonly shared = signal<AccountLogin>(emptyAccount());
  readonly split = signal(false);
  readonly sessions = signal<Record<Environment, EnvLogin>>({
    stable: emptyLogin(),
    alfa: emptyLogin(),
    beta: emptyLogin(),
    prod: emptyLogin(),
  });

  constructor() {
    if (environments.some(env => this.auth.sessions()[env.id])) this.split.set(true);
  }

  setLang(lang: 'pl' | 'en'): void {
    localStorage.setItem('lang', lang);
    document.documentElement.lang = lang;
    this.transloco.load(lang).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.transloco.setActiveLang(lang));
  }

  session(id: Environment): EnvLogin {
    return this.sessions()[id];
  }

  account(id: Environment) {
    return this.auth.sessions()[id];
  }

  signInAll(event: Event): void {
    event.preventDefault();
    const current = this.shared();
    const username = current.username.trim();
    if (current.pending || !username || !current.password) return;
    this.shared.update(form => ({ ...form, pending: true }));
    const password = current.password;
    forkJoin(environments.map(env => this.auth.login(env.id, username, password).pipe(
      map(() => ({ id: env.id, error: null as string | null })),
      catchError((error: HttpErrorResponse) => of({ id: env.id, error: this.loginError(error) })),
    ))).pipe(
      finalize(() => this.shared.update(form => ({ ...form, pending: false }))),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(results => {
      if (this.auth.complete()) {
        void this.router.navigate(['/compare']);
        return;
      }
      for (const result of results) {
        this.patch(result.id, result.error
          ? { username, password, visible: false, pending: false, error: result.error }
          : emptyLogin());
      }
      this.split.set(true);
    });
  }

  signIn(id: Environment, event: Event): void {
    event.preventDefault();
    const current = this.session(id);
    if (current.pending || !current.username.trim() || !current.password) return;
    this.patch(id, { pending: true, error: null });
    this.auth.login(id, current.username.trim(), current.password).pipe(
      finalize(() => this.patch(id, { pending: false })),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: () => {
        this.patch(id, { username: '', password: '', visible: false, error: null });
        if (this.auth.complete()) void this.router.navigate(['/compare']);
      },
      error: (error: HttpErrorResponse) => this.patch(id, { error: this.loginError(error) }),
    });
  }

  logout(id: Environment): void {
    this.auth.logout(id);
    this.patch(id, emptyLogin());
    if (environments.every(env => !this.auth.sessions()[env.id])) this.split.set(false);
  }

  patch(id: Environment, patch: Partial<EnvLogin>): void {
    this.sessions.update(all => ({ ...all, [id]: { ...all[id], ...patch } }));
  }

  private loginError(error: HttpErrorResponse): string {
    if (typeof error.error === 'string' && error.error.trim()) return error.error;
    return this.transloco.translate('login.failed');
  }
}
